<template>
  <span class="vf-o-klaviyo-tracking" aria-hidden="true" />
</template>
<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { klaviyoRouteTarget } from "~/core/store/common/klaviyo/route";
import { clearPendingKlaviyoEvents } from "~/core/store/common/klaviyo/tracking-client";

const store = useStore();
const route = useRoute();
const hasMarketingConsent = () =>
  typeof window !== "undefined" && window.vfConsent?.has("marketing") === true;
const enabled = computed(() => !!store.getters["common/klaviyo/enabled"]);
const loaded = computed(() => !!store.getters["common/klaviyo/loaded"]);
const product = computed(() => store.getters["store/product/get"]);
const category = computed(() => store.getters["store/category/get"]);
const customer = computed(() => store.getters["common/customer/get"]);
const authenticated = computed(() => store.getters["common/customer/auth"]);
const target = computed(() =>
  klaviyoRouteTarget(route, store.state.vuefront?.resolvedSeoRoute)
);
let unsubscribe: (() => void) | undefined;
let active = false;
let activating = false;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retried = false;
let lastRouteEvent = "";
let pendingEvents: Array<{ action: string; payload: any }> = [];

// Observe consented actions before settings/SDK downloads finish. Klaviyo's
// native queue then retains them until its full tracking/forms runtime arrives.
const send = (action: string, payload?: any) => {
  if (!active || !hasMarketingConsent()) return;
  if (enabled.value) void store.dispatch(`common/klaviyo/${action}`, payload);
  else if (!loaded.value) pendingEvents.push({ action, payload });
};
const trackForRoute = () => {
  if (!active || !hasMarketingConsent()) return;
  const path = route.path.replace(/\/$/, "");
  const query: any = route.query;
  const orderId = typeof query.order_id === "string" ? query.order_id : "";
  let action = "";
  let payload: any;
  if (orderId && query.redirect_status === "succeeded") {
    action = "firePlacedOrder";
    payload = orderId;
  } else if (
    target.value.type === "product" &&
    String(product.value?.id) === target.value.id
  ) {
    action = "trackViewedProduct";
    payload = JSON.parse(JSON.stringify(product.value));
  } else if (
    target.value.type === "category" &&
    String(category.value?.id) === target.value.id
  ) {
    action = "trackViewedCategory";
    payload = JSON.parse(JSON.stringify(category.value));
  } else if (path === "/search" || path.startsWith("/search/")) {
    const term = route.params.slug || query.search || query.q;
    if (typeof term === "string" && term) {
      action = "trackSearchedSite";
      payload = {
        term,
        totalResults: store.getters["store/product/list"]?.totalElements || 0,
      };
    }
  } else if (path === "/store/checkout") action = "trackStartedCheckout";
  if (!action) return;
  const key = `${route.fullPath}:${action}:${target.value.id}`;
  if (key === lastRouteEvent) return;
  lastRouteEvent = key;
  send(action, payload);
};
watch(
  () => route.fullPath,
  () => {
    lastRouteEvent = "";
    trackForRoute();
  }
);
watch(
  () => [
    target.value.type,
    target.value.id,
    product.value?.id,
    category.value?.id,
  ],
  trackForRoute
);
watch(loaded, () => {
  if (!loaded.value) return;
  const events = pendingEvents;
  pendingEvents = [];
  if (enabled.value && hasMarketingConsent())
    events.forEach((event) => send(event.action, event.payload));
});
const identifyCustomer = () => {
  if (
    !enabled.value ||
    !hasMarketingConsent() ||
    !authenticated.value ||
    !customer.value?.email
  )
    return;
  if (!store.getters["common/klaviyo/identified"])
    send("identify", customer.value);
};
watch(
  () => [enabled.value, authenticated.value, customer.value?.email],
  identifyCustomer
);
const activateKlaviyo = async () => {
  if (!active || activating || !hasMarketingConsent()) return;
  activating = true;
  try {
    await store.dispatch("common/klaviyo/load");
    if (!active || !hasMarketingConsent()) return;
    identifyCustomer();
    trackForRoute();
    if (
      !retried &&
      (!loaded.value ||
        (enabled.value && !store.getters["common/klaviyo/scriptLoaded"]))
    ) {
      retried = true;
      retryTimer = setTimeout(() => void activateKlaviyo(), 5000);
    }
  } finally {
    activating = false;
  }
};
const consentChanged = () => {
  if (hasMarketingConsent()) {
    trackForRoute();
    void activateKlaviyo();
  } else {
    clearPendingKlaviyoEvents();
    pendingEvents = [];
    lastRouteEvent = "";
    clearTimeout(retryTimer);
  }
};
const online = () => void activateKlaviyo();
const cartQuantities = () => {
  const quantities = new Map<string, number>();
  for (const item of store.getters["store/cart/get"]?.products || []) {
    const id = String(item.product?.id);
    quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity || 0));
  }
  return quantities;
};
const wishlistIds = () =>
  new Set<string>(
    (store.getters["store/wishlist/get"] || []).map((item: any) =>
      String(item.id)
    )
  );
const beforeActions = new WeakMap<object, Map<string, number> | Set<string>>();
onMounted(() => {
  active = true;
  // Leave Klaviyo's renderer-owned DOM intact. Configure heading levels in
  // the form editor instead of scanning/replacing nodes after every mutation.
  unsubscribe = store.subscribeAction({
    before: (action: any) => {
      if (!hasMarketingConsent()) return;
      if (action.type === "store/cart/add")
        beforeActions.set(action, cartQuantities());
      if (action.type === "store/wishlist/add")
        beforeActions.set(action, wishlistIds());
    },
    after: (action: any) => {
      const before = beforeActions.get(action);
      beforeActions.delete(action);
      if (!before || !hasMarketingConsent()) return;
      const items = action.payload?.product;
      const ids = new Set<string>(
        (Array.isArray(items) ? items : [items])
          .filter(Boolean)
          .map((p) => String(p.id))
      );
      if (action.type === "store/cart/add") {
        const after = cartQuantities();
        ids.forEach((id) => {
          const quantity =
            (after.get(id) || 0) -
            ((before as Map<string, number>).get(id) || 0);
          if (quantity > 0)
            send("trackAddedToCart", { productId: id, quantity });
        });
      } else if (action.type === "store/wishlist/add") {
        const after = wishlistIds();
        ids.forEach((id) => {
          if (!before.has(id) && after.has(id))
            send("trackAddedToWishlist", id);
        });
      }
    },
  });
  window.addEventListener("vf:consent-updated", consentChanged);
  window.addEventListener("online", online);
  trackForRoute();
  void activateKlaviyo();
});
onBeforeUnmount(() => {
  active = false;
  pendingEvents = [];
  clearTimeout(retryTimer);
  unsubscribe?.();
  window.removeEventListener("vf:consent-updated", consentChanged);
  window.removeEventListener("online", online);
});
</script>
